import eventMixin from '../lib/mixins/event';
import Logger from '../lib/logger';
import Player from '@vimeo/player';
import { v4 as uuidv4 } from 'uuid';

/**
 * ThemeVideoBaner
 *
 * Supports two video sources (see snippets/theme-video-banner.liquid):
 * - Native MP4 (`[data-theme-video-banner-native]`): a server-rendered
 *   <video> element with preload="none". The Vimeo player is NEVER
 *   instantiated in this mode, so no Vimeo iframe/scripts are loaded.
 * - Vimeo (`[data-theme-video-banner-video]`): the original behavior; the
 *   Vimeo player (and its iframe) is only created once the banner scrolls
 *   into view.
 * - YouTube (`[data-theme-video-banner-youtube]`): a privacy-enhanced embed
 *   created only when visible and controlled through the iframe API.
 *
 * Both modes share the same play button, placeholder image, and
 * play/pause/ended behavior.
 */
export default class ThemeVideoBaner {

    constructor(dom) {

        // Vars
        // ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
        this.NAME = `ThemeVideoBaner`;

        this.nativeVideoDom = dom.querySelector('[data-theme-video-banner-native]');
        this.vimeoDom = dom.querySelector('[data-theme-video-banner-video]');
        this.youtubeDom = dom.querySelector('[data-theme-video-banner-youtube]');
        this.videoDom = this.nativeVideoDom || this.vimeoDom || this.youtubeDom;

        if (!this.videoDom) return;

        if (this.vimeoDom && !this.nativeVideoDom) {
            const uuid = uuidv4();
            this.vimeoDom.setAttribute('id', uuid);
            this.vimeoId = this.vimeoDom.getAttribute('data-theme-video-banner-video')?.trim();
            this.vimeoUrl = /^https?:\/\//i.test(this.vimeoId)
                ? this.vimeoId
                : `https://vimeo.com/${this.vimeoId}`;
        }

        if (this.youtubeDom) {
            this.youtubeId = this.youtubeDom.getAttribute('data-theme-video-banner-youtube');
            this.youtubePlaying = false;
            this.youtubeIframe = null;
            this.youtubeMessageHandler = null;
        }

        this.autoplay = this.videoDom.getAttribute(`data-theme-video-banner-video-autoplay`);
        this.showPlayerControls = this.videoDom.getAttribute(`data-theme-video-banner-player-controls`) === 'true';

        this.placeholderImageDom = dom.querySelector(`[data-theme-video-banner-placeholder-image]`);
        this.placeholderImageDomHiddenClass = `theme-video-banner__image--hidden`;
        this.vimeoPosterDom = dom.querySelector(`[data-theme-video-banner-vimeo-poster]`);
        this.youtubePosterImageDom = dom.querySelector(`[data-theme-video-banner-youtube-poster]`);

        this.playButtonDom = dom.querySelector('theme-play-button');
        this.muteButtonDom = dom.querySelector('[data-theme-video-banner-mute-button]');
        this.videoContainerDom = dom.querySelector('.theme-video-banner__video-container');

        this.videoPlayer = null;
        this.playerInitialized = false;
    }

    // Logic
    // ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
    init() {
        Logger.info(`${this.NAME} > init`);

        if (!this.videoDom) return;

        const self = this;
        const autoplayBool = (this.autoplay === 'true');

        this.vimeoPosterPromise = this.loadVimeoPoster();
        this.loadYouTubePoster();

        // Page-video mode: keep the poster visible and avoid creating an
        // iframe or loading native video data until the visitor presses play.
        if (!autoplayBool) {
            this.showPlayButton();
            const initializeOnClick = async (event) => {
                if (event.target.closest?.('[data-theme-video-banner-mute-button]')) return;
                if (this.playerInitialized) return;

                event.preventDefault();
                this.videoContainerDom.removeEventListener('click', initializeOnClick);
                this.playerInitialized = true;

                await this.vimeoPosterPromise;

                if (self.nativeVideoDom) {
                    self.initializeNativePlayer(true);
                } else if (self.youtubeDom) {
                    self.initializeYouTubePlayer(true);
                } else {
                    self.initializeVideoPlayer(true);
                }
            };
            this.videoContainerDom.addEventListener('click', initializeOnClick);
            return;
        }

        /**
        * Initialize the player when visible (Vimeo: creates the iframe;
        * native: starts autoplay / warms up metadata)
        */
        const observer = new IntersectionObserver(entries => {
            entries.forEach(async entry => {
                const intersecting = entry.isIntersecting;

                if (intersecting && !this.playerInitialized) {
                    this.playerInitialized = true;

                    await this.vimeoPosterPromise;

                    if (self.nativeVideoDom) {
                        self.initializeNativePlayer();
                    } else if (self.youtubeDom) {
                        self.initializeYouTubePlayer();
                    } else {
                        self.initializeVideoPlayer();
                    }
                }
            })
        })

        observer.observe(this.videoDom);

    }

    async loadVimeoPoster() {
        if (!this.vimeoPosterDom || !this.vimeoId) return;

        const imageDom = this.vimeoPosterDom.querySelector('img');

        try {
            const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(this.vimeoUrl)}&width=1920`);
            if (!response.ok) throw new Error(`Vimeo oEmbed request failed: ${response.status}`);

            const video = await response.json();
            if (video.thumbnail_url && imageDom) {
                imageDom.src = video.thumbnail_url;
                imageDom.alt = video.title || '';
                if (typeof imageDom.decode === 'function') {
                    await imageDom.decode();
                }
            }
        } catch (error) {
            Logger.error(`${this.NAME} > loadVimeoPoster`, error);
        }
    }

    loadYouTubePoster() {
        if (!this.youtubePosterImageDom) return;

        const imageDom = this.youtubePosterImageDom;
        const maxresSrc = imageDom.dataset.maxresSrc;
        const fallbackSrc = imageDom.dataset.fallbackSrc;
        const maxresImage = new Image();

        const useFallback = () => {
            imageDom.src = fallbackSrc;
        };

        maxresImage.addEventListener('load', () => {
            // Missing max-resolution thumbnails return YouTube's generic
            // 120x90 placeholder with a successful HTTP response.
            imageDom.src = maxresImage.naturalWidth >= 640
                ? maxresSrc
                : fallbackSrc;
        }, { once: true });
        maxresImage.addEventListener('error', useFallback, { once: true });
        maxresImage.src = maxresSrc;
    }

    hidePlayButton() {
        Logger.info(`${this.NAME} > hidePlayButton`);
        if (!this.playButtonDom) return;
        this.playButtonDom.dataset.playing = 'true';
        if (this.showPlayerControls && this.playerInitialized) {
            this.playButtonDom.setAttribute('hidden', '');
        }
    }

    /**
     * initializeNativePlayer - Native <video> (MP4) mode. No Vimeo code runs.
     */
    initializeNativePlayer(playOnInit = false) {
        Logger.info(`${this.NAME} > initializeNativePlayer`);

        const self = this;
        const video = this.nativeVideoDom;
        this.muteButtonDom?.removeAttribute('hidden');

        this.muteButtonDom?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            video.muted = !video.muted;
            self.updateMuteButton(video.muted);
        });

        video.addEventListener('volumechange', function () {
            self.updateMuteButton(video.muted || video.volume === 0);
        });

        // Listen for ended
        video.addEventListener('ended', function () {
            self.showPlayButton();
            self.showPlaceholderImage();
            video.currentTime = 0;
        });

        // Listen for play
        video.addEventListener('play', function () {
            self.hidePlayButton();
            self.hidePlaceholderImage();
        });

        // Listen for pause (ended also fires pause; showPlayButton is idempotent)
        video.addEventListener('pause', function () {
            self.showPlayButton();
        });

        // Check for autoplay
        const autoplayBool = (this.autoplay === 'true');
        if (autoplayBool) {
            Logger.info(`${this.NAME} > initializeNativePlayer > autoplay`);
            this.hidePlayButton();
            video.muted = true;
            const promise = video.play();
            if (promise && promise.catch) {
                // Autoplay blocked (e.g. Low Power Mode): fall back to the play button
                promise.catch(() => self.showPlayButton());
            }
        } else if (playOnInit) {
            video.play().catch(() => self.showPlayButton());
        } else {
            // Warm up dimensions/first frame without downloading the full file
            video.preload = 'metadata';
            this.showPlayButton();
        }
    }

    initializeVideoPlayer(playOnInit = false) {
        Logger.info(`${this.NAME} > initializeVideoPlayer`);

        const self = this;
        const autoplayBool = (this.autoplay === 'true');
        this.muteButtonDom?.removeAttribute('hidden');

        this.videoPlayer = new Player(this.vimeoDom.getAttribute('id'), {
            url: this.vimeoUrl,
            controls: this.showPlayerControls,
            title: false,
            responsive: true,
            background: false,
            byline: false,
            portrait: false,
            playsinline: true,
            autopause: false,
            muted: autoplayBool,
        });

        this.muteButtonDom?.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const muted = this.muteButtonDom.dataset.muted === 'true';

            try {
                if (typeof this.videoPlayer.setMuted === 'function') {
                    await this.videoPlayer.setMuted(!muted);
                } else {
                    await this.videoPlayer.setVolume(muted ? 1 : 0);
                }
                self.updateMuteButton(!muted);
            } catch (error) {
                Logger.error(`${this.NAME} > toggleMute`, error);
            }
        });

        this.videoPlayer.on('volumechange', function (data) {
            self.updateMuteButton(data.muted === true || data.volume === 0);
        });

        // Listen for ended
        this.videoPlayer.on('ended', function () {
            self.showPlayButton();
            self.showPlaceholderImage();
            self.videoPlayer.setCurrentTime(0);
        });

        // Listen for play
        this.videoPlayer.on('play', function () {
            self.hidePlayButton();
            self.hidePlaceholderImage();
        });

        // Listen for pause
        this.videoPlayer.on('pause', function () {
            self.showPlayButton();
        });

        // Check for autoplay
        if (autoplayBool) {
            Logger.info(`${this.NAME} > initializeVideoPlayer > autoplay`);
            this.hidePlayButton();
            this.muteVideo();
            this.playVideo();
        } else if (playOnInit) {
            this.hidePlayButton();
            this.playVideo();
        } else {
            this.showPlayButton();
        }
    }

    initializeYouTubePlayer(playOnInit = false) {
        Logger.info(`${this.NAME} > initializeYouTubePlayer`);

        const autoplayBool = (this.autoplay === 'true');
        const shouldPlay = autoplayBool || playOnInit;
        this.muteButtonDom?.removeAttribute('hidden');
        const params = new URLSearchParams({
            autoplay: shouldPlay ? '1' : '0',
            controls: this.showPlayerControls ? '1' : '0',
            enablejsapi: '1',
            playsinline: '1',
            rel: '0',
            mute: autoplayBool ? '1' : '0',
            origin: window.location.origin,
        });

        const iframe = document.createElement('iframe');
        iframe.id = uuidv4();
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.youtubeId)}?${params.toString()}`;
        iframe.title = 'YouTube video player';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.setAttribute('frameborder', '0');
        this.youtubeDom.appendChild(iframe);
        this.youtubeIframe = iframe;
        this.youtubePlaying = shouldPlay;

        const sendCommand = (command, args = []) => {
            iframe.contentWindow?.postMessage(JSON.stringify({
                event: 'command',
                func: command,
                args,
            }), '*');
        };

        iframe.addEventListener('load', () => {
            iframe.contentWindow?.postMessage(JSON.stringify({
                event: 'listening',
                id: iframe.id,
            }), '*');
            if (shouldPlay) {
                if (autoplayBool) sendCommand('mute');
                sendCommand('playVideo');
                this.hidePlayButton();
                this.hidePlaceholderImage();
            } else {
                this.showPlayButton();
            }
        });

        this.youtubeMessageHandler = (event) => {
            if (event.source !== iframe.contentWindow) return;

            let message = event.data;
            if (typeof message === 'string') {
                try {
                    message = JSON.parse(message);
                } catch (error) {
                    return;
                }
            }

            if (message?.event !== 'onStateChange') return;

            if (message.info === 1) {
                this.youtubePlaying = true;
                this.hidePlayButton();
                this.hidePlaceholderImage();
            } else if (message.info === 0 || message.info === 2) {
                this.youtubePlaying = false;
                this.showPlayButton();
                if (message.info === 0) {
                    sendCommand('seekTo', [0, true]);
                    this.showPlaceholderImage();
                }
            }
        };
        window.addEventListener('message', this.youtubeMessageHandler);

        this.muteButtonDom?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const muted = this.muteButtonDom.dataset.muted === 'true';
            sendCommand(muted ? 'unMute' : 'mute');
            this.updateMuteButton(!muted);
        });

    }

    hidePlaceholderImage() {
        Logger.info(`${this.NAME} > hidePlaceholderImage`);
        this.placeholderImageDom?.classList.add(this.placeholderImageDomHiddenClass);
    }

    muteVideo() {
        Logger.info(`${this.NAME} > muteVideo`);
        if (typeof this.videoPlayer?.setMuted === 'function') {
            this.videoPlayer.setMuted(true);
        } else {
            this.videoPlayer?.setVolume(0);
        }
        this.updateMuteButton(true);
    }

    updateMuteButton(muted) {
        if (!this.muteButtonDom) return;

        this.muteButtonDom.dataset.muted = muted ? 'true' : 'false';
        this.muteButtonDom.setAttribute('aria-pressed', muted ? 'true' : 'false');
        this.muteButtonDom.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    playVideo() {
        Logger.info(`${this.NAME} > playVideo`);
        this.videoPlayer?.play();
    }

    pauseVideo() {
        Logger.info(`${this.NAME} > pauseVideo`);
        this.videoPlayer?.pause();
    }

    showPlaceholderImage() {
        Logger.info(`${this.NAME} > showPlaceholderImage`);
        this.placeholderImageDom?.classList.remove(this.placeholderImageDomHiddenClass);
    }

    showPlayButton() {
        Logger.info(`${this.NAME} > showPlayButton`);
        if (!this.playButtonDom) return;
        if (this.showPlayerControls && this.playerInitialized) {
            this.playButtonDom.setAttribute('hidden', '');
            return;
        }
        this.playButtonDom.removeAttribute('hidden');
        this.playButtonDom.dataset.playing = 'false';
    }
}

// Mixins
Object.assign(ThemeVideoBaner.prototype, eventMixin);
